import React, { useCallback, useMemo, forwardRef, useRef, useState } from 'react';

import { Box } from '@chakra-ui/react';
import { Popper } from '@material-ui/core';
import Dropdown from 'components/DateTimePicker/Dropdown';
import styled, { css } from 'styled-components';
import {
  ColoredCircularProgress,
  StyledBorderedPaper,
  StyledBox,
  StyledGrid,
  StyledTooltip,
  StyledTypography,
  VerticallyAlignedFlexContainer,
} from 'styles/common';

import { Union } from 'assets/assetsV1/iconComponents/Union';

import theme from 'constants/theme';

import { Typography8 } from 'uiToolkit/Typography';
import { Button } from 'uiToolkitV1/Button';

import { useGoalRecurrenceDetails, RecurrenceActions } from './Context/GoalRecurrenceDetailsProvider';

const WEEKDAYS = [
  { id: 1, display: 'Monday' },
  { id: 2, display: 'Tuesday' },
  { id: 3, display: 'Wednesday' },
  { id: 4, display: 'Thursday' },
  { id: 5, display: 'Friday' },
  { id: 6, display: 'Saturday' },
  { id: 0, display: 'Sunday' },
];

const RecurrenceInfoContainer = forwardRef(
  ({ text = 'Recurrence details', togglePopup = null, loading, isOpen, isDisabled = false }, ref) => {
    const buttonContentColor = isDisabled
      ? `${theme.palette.primary.main}40`
      : isOpen
        ? 'white'
        : theme.palette.primary.main;
    return (
      <StyledRecurrenceInfoContainer isDisabled={isDisabled} isOpen={isOpen} ref={ref}>
        {loading ? (
          <StyledBox width="20ch" display="flex" align="center" justifyContent="center">
            <ColoredCircularProgress color={theme.palette.primary.main} size="smallest" padding="6px" />
          </StyledBox>
        ) : (
          <>
            {/* <StyledIcon
              color={isOpen ? 'white' : theme.palette.text.navyBlue}
              size="tinier"
              leftGutter={2}
            >
              <EventIcon />
            </StyledIcon> */}
            <StyledBox marginX={1} marginY={10} width="16rem" textAlign="center">
              <StyledTypography customColor={buttonContentColor} leftGutter={2} rightGutter={2} onClick={togglePopup}>
                {text}
              </StyledTypography>
            </StyledBox>
          </>
        )}
        <StyledBox width="0" height="1rem" border={`1px solid ${theme.palette.borders.lighterGrey}`} />
      </StyledRecurrenceInfoContainer>
    );
  }
);

const GoalReferenceDetails = ({
  teamId = {},
  isDisabled,
  isIcon = false,
  iconStyleProps = {},
  IconComponent = null,
}) => {
  const { recurrenceText, reset, submit, loading, recurrenceDispatch } = useGoalRecurrenceDetails();
  // const { currentTask } = useContext(TaskDataContext);
  const [isOpen, setOpen] = useState(false);
  const anchorRef = useRef(null);

  const togglePopup = () => {
    setOpen((status) => !status);
  };

  const cancelEdting = useCallback(() => {
    reset();
    setOpen(false);
  }, [recurrenceDispatch, reset, setOpen]);

  const updateRecurrence = useCallback(() => {
    submit();
    setOpen(false);
  }, [recurrenceDispatch, submit, setOpen]);

  if (isIcon) {
    return (
      <>
        <div
          style={{ cursor: 'pointer', margin: '6px 5px 0 0', ...iconStyleProps }}
          onClick={() => togglePopup()}
          ref={anchorRef}
        >
          <StyledTooltip title={`Team Members will receive check-in reminders : ${recurrenceText}.`}>
            {IconComponent ? (
              <IconComponent />
            ) : (
              <Box>
                <Union boxSize="20px" color="#3F8CE9" />
              </Box>
            )}
          </StyledTooltip>
        </div>
        <RecurrenceChangeDropdown
          isOpen={isOpen}
          anchorEl={anchorRef.current}
          onCancel={cancelEdting}
          onSubmit={updateRecurrence}
        />
      </>
    );
  }

  return (
    <StyledBox>
      <RecurrenceInfoContainer
        parentCheckInId={teamId}
        text={recurrenceText}
        ref={anchorRef}
        loading={loading}
        isOpen={isOpen}
        togglePopup={togglePopup}
        isDisabled={isDisabled}
      />
      <RecurrenceChangeDropdown
        isOpen={isOpen}
        anchorEl={anchorRef.current}
        onCancel={cancelEdting}
        onSubmit={updateRecurrence}
      />
    </StyledBox>
  );
};

export const RecurrenceChangeDropdown = ({ isOpen, onCancel, onSubmit, anchorEl }) => {
  const { state, recurrenceDispatch } = useGoalRecurrenceDetails();

  const { recurrencePeriod, weeklyRecurrenceDetails, monthlyRecurrenceDetails } = state;

  //   const timeChangeHandler = useCallback(
  //     timeObj => {
  //       recurrenceDispatch({
  //         type: RecurrenceActions.UPDATE_TIME,
  //         payload: { time: timeObj }
  //       });
  //     },
  //     [recurrenceDispatch]
  //   );

  //   const durationChangeHandler = useCallback(
  //     duration => {
  //       recurrenceDispatch({
  //         type: RecurrenceActions.UPDATE_DURATION,
  //         payload: { duration }
  //       });
  //     },
  //     [recurrenceDispatch]
  //   );

  return (
    <StyledPopper disablePortal open={isOpen} anchorEl={anchorEl} placement="bottom" transition>
      <StyledBorderedPaper padding="1rem" style={{ width: '45ch', minWidth: '350px', marginTop: '0.5rem' }}>
        <MonthWeekDropdown period={recurrencePeriod?.period} recurrenceDispatch={recurrenceDispatch} />
        {recurrencePeriod?.period === 'NONE' ? <></> : null}
        {recurrencePeriod?.period === 'WEEK' ? (
          <WeeklyRecurrenceComp details={weeklyRecurrenceDetails} recurrenceDispatch={recurrenceDispatch} />
        ) : null}
        {recurrencePeriod?.period === 'MONTH' ? (
          <MonthlyRecurrenceComp
            currentPeriodType={recurrencePeriod?.period}
            details={monthlyRecurrenceDetails}
            recurrenceDispatch={recurrenceDispatch}
          />
        ) : null}
        {recurrencePeriod?.period === 'FORTNIGHTLY' ? (
          <MonthlyRecurrenceComp
            currentPeriodType={recurrencePeriod?.period}
            details={monthlyRecurrenceDetails}
            recurrenceDispatch={recurrenceDispatch}
          />
        ) : null}
        {/* <TimeAndDurationComp
          time={time}
          duration={duration}
          onTimeChange={timeChangeHandler}
          onDurationChange={durationChangeHandler}
        /> */}
        <StyledGrid container alignItems="center" justify="flex-end" topGutter={2}>
          {/* <StyledButton
            variant="outlined"
            rightGutter={2}
            color="primary"
            borderColor="primary"
            onClick={onCancel}
            style={{ width: '12ch' }}
          >
            Cancel
          </StyledButton> */}
          <Button variant="outlined" style={{ width: '12ch' }} mr={3} onClick={onCancel}>
            Cancel
          </Button>
          {/* <StyledButton
            fontColor="white"
            customColor="primary"
            style={{ width: '12ch' }}
            onClick={onSubmit}
          >
            Save
          </StyledButton> */}
          <Button variant="rowButtonFilled" onClick={onSubmit} style={{ width: '12ch' }}>
            Save
          </Button>
        </StyledGrid>
      </StyledBorderedPaper>
    </StyledPopper>
  );
};

const MonthWeekDropdown = ({ period, recurrenceDispatch }) => {
  const recurrencePeriods = useMemo(
    () => [
      {
        id: 'NONE',
        display: 'None',
      },
      {
        id: 'WEEK',
        display: 'Weekly',
      },
      {
        id: 'FORTNIGHTLY',
        display: 'Fortnightly',
      },
      {
        id: 'MONTH',
        display: 'Monthly',
      },
    ],
    []
  );

  const changeRecurrencePeriod = useCallback(
    (periodObj) => {
      recurrenceDispatch({
        type: RecurrenceActions.UPDATE_RECURRENCE_PERIOD,
        payload: {
          period: periodObj.id,
        },
      });
    },
    [recurrenceDispatch]
  );

  return (
    <StyledGrid topGutter={2} container alignItems="center">
      <StyledGrid item xs={8}>
        <StyledTypography variant="body2" truncate>
          Check-in reminders frequency
        </StyledTypography>
      </StyledGrid>
      <StyledGrid item xs={4}>
        <Dropdown
          fullWidth
          allOptions={recurrencePeriods}
          selectedValue={recurrencePeriods.filter((p) => p.id === period)?.[0]}
          onChange={changeRecurrencePeriod}
          optionFormatter={(value) => value.display}
        />
      </StyledGrid>
    </StyledGrid>
  );
};

const WeeklyRecurrenceComp = ({ details, recurrenceDispatch }) => {
  const { day } = details;

  const changeWeekday = useCallback(
    (idx) => {
      recurrenceDispatch({
        type: RecurrenceActions.UPDATE_WEEKLY_RECURRENCE_DAY,
        payload: {
          day: idx,
        },
      });
    },
    [recurrenceDispatch]
  );

  // const getNextDayOfWeek = (date, dayOfWeek) => {
  //   const resultDate = new Date(date.getTime());
  //   resultDate.setDate(date.getDate() + ((7 + dayOfWeek - date.getDay()) % 7));
  //   return resultDate;
  // };

  // const startingDate = () => {
  //   const date = getNextDayOfWeek(new Date(Date.now()), day);
  //   return moment(date).format('MMM Do YY');
  // };

  return (
    <>
      <StyledGrid container alignItems="center" justify="space-between" topGutter={2} bottomGutter={2}>
        {WEEKDAYS.map(({ id, display }) => (
          <StyledDayButton key={id} isSelected={id === day} onClick={() => changeWeekday(id)}>
            {display[0]}
          </StyledDayButton>
        ))}
      </StyledGrid>
      <VerticallyAlignedFlexContainer>
        {/* <InfoIcon2 color={theme.palette.icons.grey2} size="fixed16px" /> */}
        <Typography8 color={theme.palette.text.label} leftGutter={1}>
          {/* <span>Reminders start from {startingDate()}</span> */}
        </Typography8>
      </VerticallyAlignedFlexContainer>
    </>
  );
};

const MonthlyRecurrenceComp = ({ details, recurrenceDispatch, currentPeriodType }) => {
  const WEEK_NUMBERS = useMemo(() => [
    { id: 0, display: 'First' },
    { id: 1, display: 'Second' },
    { id: 2, display: 'Third' },
    { id: 3, display: 'Fourth' },
    { id: 4, display: 'Last' },
  ]);

  const changeMonthlyRecurrenceDetails = useCallback((type, value) => {
    switch (type) {
      case 'recurrenceType':
        recurrenceDispatch({
          type: RecurrenceActions.UPDATE_MONTHLY_RECURRENCE_TYPE,
          payload: {
            recurrenceType: value,
          },
        });
        break;
      case 'dayOfMonth':
        recurrenceDispatch({
          type: RecurrenceActions.UPDATE_MONTHLY_RECURRENCE_DAY_OF_MONTH,
          payload: {
            dayOfMonth: value,
          },
        });
        break;
      case 'dayOfWeek':
        recurrenceDispatch({
          type: RecurrenceActions.UPDATE_MONTHLY_RECURRENCE_DAY_OF_WEEK,
          payload: {
            dayOfWeek: value,
          },
        });
        break;
      case 'weekNumber':
        recurrenceDispatch({
          type: RecurrenceActions.UPDATE_MONTHLY_RECURRENCE_WEEK_NUMBER,
          payload: {
            weekNumber: value,
          },
        });
        break;
    }
  });

  const { dayOfWeek, weekNumber } = details;

  return (
    <StyledGrid container alignItems="center" topGutter={1.5}>
      {/* <StyledGrid item xs="2">
          <Checkbox
            color="primary"
            value="DAY_OF_MONTH"
            checked={recurrenceType === 'DAY_OF_MONTH'}
            onChange={({ target }) => {
              changeMonthlyRecurrenceDetails('recurrenceType', target.value);
            }}
            disableRipple
            icon={
              <StyledIcon size="smaller">
                <RadioButtonUncheckedRoundedIcon />
              </StyledIcon>
            }
            checkedIcon={
              <StyledIcon size="smaller" color="primary">
                <CheckCircleRoundedIcon />
              </StyledIcon>
            }
          />
        </StyledGrid> */}

      {/* OLD VERSION */}
      {/* {currentPeriodType === 'MONTH' && (
          <StyledGrid item xs="12">
            <Dropdown
              fullWidth
              disabled={false}
              allOptions={DAYS_OF_MONTH}
              selectedValue={dayOfMonth}
              onChange={value => changeMonthlyRecurrenceDetails('dayOfMonth', value)}
              optionFormatter={value => `Day ${String(value).padStart(2, 0)}`}
            />
          </StyledGrid>
        )} */}

      {(currentPeriodType === 'FORTNIGHTLY' || currentPeriodType === 'MONTH') && (
        <StyledGrid container alignItems="center" bottomGutter={1.5}>
          {/* <StyledGrid item xs="2">
              <Checkbox
                color="primary"
                value="DAY_OF_WEEK"
                checked={recurrenceType === 'DAY_OF_WEEK'}
                onChange={({ target }) => {
                  changeMonthlyRecurrenceDetails('recurrenceType', target.value);
                }}
                disableRipple
                icon={
                  <StyledIcon size="smaller">
                    <RadioButtonUncheckedRoundedIcon />
                  </StyledIcon>
                }
                checkedIcon={
                  <StyledIcon size="smaller" color="primary">
                    <CheckCircleRoundedIcon />
                  </StyledIcon>
                }
              />
            </StyledGrid> */}
          <StyledGrid item xs="6" style={{ paddingRight: '0.5rem' }}>
            <Dropdown
              fullWidth
              disabled={false}
              allOptions={WEEK_NUMBERS}
              selectedValue={WEEK_NUMBERS.filter((wn) => wn.id === weekNumber)?.[0]}
              onChange={(value) => changeMonthlyRecurrenceDetails('weekNumber', value.id)}
              optionFormatter={(value) => value.display}
            />
          </StyledGrid>
          <StyledGrid item xs="6" style={{ paddingLeft: '0.5rem' }}>
            <Dropdown
              fullWidth
              disabled={false}
              allOptions={WEEKDAYS}
              selectedValue={WEEKDAYS.filter(({ id }) => id === dayOfWeek)?.[0]}
              onChange={(value) => changeMonthlyRecurrenceDetails('dayOfWeek', value.id)}
              optionFormatter={(value) => value.display}
            />
          </StyledGrid>
        </StyledGrid>
      )}
    </StyledGrid>
  );
};

const StyledDayButton = styled.div`
  width: 4ch;
  height: 4ch;
  border-radius: 50%;

  display: flex;
  align-items: center;
  justify-content: center;

  color: ${(props) => props.theme.palette.borders.mediumGrey};
  border: ${(props) => `1px solid ${props.theme.palette.borders.mediumGrey}`};

  cursor: pointer;

  ${(props) =>
    props.isSelected &&
    css`
      border: none;
      color: ${props.theme.palette.primary.main};
      background-color: ${props.theme.palette.primary.main}33;
    `};
`;

const StyledRecurrenceInfoContainer = styled.div`
  background-color: ${(props) => props.isOpen && props.theme.palette.primary.main};
  border: 1px solid
    ${(props) => (props.isDisabled ? `${props.theme.palette.primary.main}40` : props.theme.palette.primary.main)};
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  ${(props) =>
    props.isDisabled &&
    css`
      pointer-events: none;
    `}
`;

const StyledPopper = styled(Popper)`
  z-index: 999;
`;

export default GoalReferenceDetails;
